import { PageModuleGroupModules } from './module.types';
import { PageModule } from './module.types';
import { TranslateString } from './translation.types';
import { CreateVideo, VideoPlayerControlsSettings } from './video.types';
import * as ThemePackTypes from './theme-packs.types';
import { BLAdmin, Profile } from './profile.types';

export type TSessionUuid = string;

export interface Session {
  addedToMyList?: boolean;
  /** @deprecated use custom_url */
  backup_url?: string;
  /** @deprecated use custom_url_enabled */
  backup_url_enabled?: boolean;
  breakout_room_order?: number[];
  breakout_rooms?: BreakoutRoom[];
  breakout_session?: IBreakoutSession;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  breakout_session_hosts?: any;
  broadcast_type: EBroadcastTypes | null;
  channel: number;
  courses_settings: SessionCoursesSettings;
  current_language: string;
  custom_css?: string;
  custom_css_compiled: string | null;
  custom_url_enabled?: boolean;
  custom_url?: string;
  default_language: string; // should eventually use LanguagesAbbr instead
  description: TranslateString | null;
  enable_feedback_survey?: boolean;
  enable_share?: boolean;
  end_timestamp: number | null;
  firesides_hosts: string[] | Record<string, unknown>[];
  hosts?: Profile[];
  image?: string | null;
  is_on?: boolean;
  // ivs_channel?: IvsChannel;
  languages: string[];
  layout: SessionLayout;
  moderation: ISessionModeration | null;
  module_grouping?: PageModuleGroupModules[];
  modules: PageModule[];
  /** @deprecated on_demand will eventually be removed in place of session_type */
  on_demand?: boolean; // will eventually be removed in place of session_type
  /** @deprecated on_demand_video replaced by static_video  */
  on_demand_video: CreateVideo[] | null;
  /** key is language, value is static video uuid */
  static_videos: Record<string, string> | null;
  opentok_id: string | null;
  opentok_id_issued_timestamp: number | null;
  post_broadcast_thumbnail?: string | null;
  pre_broadcast_thumbnail?: string | null;
  reaction_settings?: ReactionSettings;
  replay_on?: boolean;
  roomBackground?: string;
  rooms?: IBreakoutChatRoom[];
  session: number;
  session_chat_dark_mode_enabled: boolean;
  session_chat_enabled: boolean;
  session_chat_single_language?: boolean;
  session_type: SessionTypesEnum;
  should_record: boolean | null;
  streaming_options: SessionStreamingOptions;
  tags?: string[] /** uuid[] of event tag */;
  timestamp: number | null;
  title: TranslateString;
  tracks: Categories | null;
  translate_all?: boolean;
  use_different_thumbnails?: boolean;
  use_safe_css: boolean; // default false in database, will always be defined
  uuid: string;
  video: SessionVideo;
  video_duration?: number;
  video_player_controls_settings?: VideoPlayerControlsSettings;
  waitingRoom?: {
    slides: string[];
    startBefore: number;
  };
  calendar_settings?: {
    auto_send_calendar_invite?: boolean | null;
    content?: {
      [key: string]: {
        // Language code
        title?: string;
        description?: string;
      };
    };
  };
  fireside_session_settings?: FiresideSessionSettings;
  session_chat_style?: SessionTabsChatStyles | null;
  internal_descriptor?: string | null;
  chat_content_settings?: SessionChatContentSettings | null;
  requires_time_change?: TRequiresTimeChange | null;
  crew?: ISessionCrew[] | null;
  embed_code_enabled?: boolean;
  // New parameters must be optional or nullable
}

export type TPartialSessionWithUuid = Partial<Session> & { uuid: string };

export interface BreakoutRoom {
  id?: number;
  uuid: string;
  session: number;
  name: string;
  max_users: number;
  roomAtCapacity?: boolean;
  type: 'interest' | 'random' | 'assigned';
  description?: string;
  image: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  video_settings: any;
  imageUploading: boolean;
  nameFocused: boolean;
  closed: boolean;
  name_translations?: TranslateString;
  description_translations?: TranslateString;
  // New parameters must be optional or nullable
}

export interface IBreakoutSession {
  session: number;
  create_room_when_full: boolean;
  banner_image: string | undefined;
  use_background_image: boolean;
  background_image: string | null;
  use_zoom: boolean;
  use_google_meet?: boolean;
  // New parameters must be optional or nullable
}

export enum EBroadcastTypes {
  greenroom = 'greenroom',
  rtmps = 'rtmps',
  embed = 'embed',
  fireside = 'fireside',
}

export interface SessionLayout {
  /** @deprecated withMenu was used for determining the tab layout, now the tabs field is managing that. Keeping it as to not break previous events */
  withMenu: boolean;
  overlayChat: boolean;
  tabs: SessionTabsLayout;
  styling_overrides?: {
    background?: {
      /** background color should only apply to tab header portion of page */ color?: string;
      image?: {
        is_on: boolean;
        src: string;
      };
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    content?: any;
    /** session colors override event colors on session pages if they exist */
    colors?: ThemePackTypes.ISessionColors;
  };
  display_viewer_count?: boolean;
  custom_social_links?: CustomSocialLinks;
  // New parameters must be optional or nullable
}

export interface ISessionModeration {
  posts: boolean; // determines if the posts require moderator approval or not
  questions: boolean; // determines if the questions require moderator approval or not
  chat: boolean; // determines if the chat requires moderator approval or not
  use_blocked_words_filter?: boolean; // should use word filter
}

export interface ReactionSettings {
  enabled: boolean;
  show_descriptions?: boolean;
  reactions: ReactionConfig[];
  // New parameters must be optional or nullable
}

export interface ReactionConfig {
  enabled: boolean;
  color: string;
  opacity: number;
  icon?: string; // Defined if non-custom reaction
  url?: string; // S3 url if custom reaction
  name: string;
  description?: string;
  // New parameters must be optional or nullable
}

export interface IBreakoutChatRoom {
  title: string;
  participants_joined: Profile[];
  presenters?: Profile[];
  time_before_switch?: number;
  max_participants?: number;
  // New parameters must be optional or nullable
}

export enum SessionTypesEnum {
  onDemand = 'on-demand',
  broadcast = 'broadcast',
  breakoutRooms = 'breakout-rooms',
  fireside = 'fireside',
}

export const SESSION_TYPE_LABELS: Record<SessionTypesEnum, string> = {
  [SessionTypesEnum.onDemand]: 'On demand',
  [SessionTypesEnum.broadcast]: 'Broadcast',
  [SessionTypesEnum.breakoutRooms]: 'Breakout rooms',
  [SessionTypesEnum.fireside]: 'Fireside',
};

export type RtmpSource = 'teams' | 'zoom' | 'encoder';

export interface SessionStreamingOptions {
  test_stream?: boolean;
  single_stream: boolean;
  auto_transcripts?: boolean;
  watermark?: boolean;
  stylepackId?: string;
  drm_required?: boolean;
  rtmp_source?: RtmpSource;
  do_not_record?: boolean;
  // New parameters must be optional or nullable
}

export interface SessionCoursesSettings {
  enabled: boolean;
}

export interface SessionVideo {
  url?: string;
  compiled_url?: string; // url + '/' + fileds.Key
  fields?: {
    Key?: string;
    bucket?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  type?: SessionVideoTypes;
  customizations?: SessionVideoCustomizations;
  // New parameters must be optional or nullable
}

export enum SessionVideoTypes {
  greenroom,
  custom,
}

export interface SessionVideoCustomizations {
  backgroundColor?: ThemePackTypes.ColorKeys;
  backgroundImage?: string;
  // New parameters must be optional
}

export interface FiresideSessionSettings {
  uuid: Session['uuid'];
  settings?: {
    at_capacity?: boolean;
    google_meet?: {
      name: string;
      meetingUri: string;
    };
    google_meet_code?: string;
    google_meet_refresh_token?: string;
    google_meet_site_url?: string;
    google_meet_use_service_account?: boolean;
    use_google_meet?: boolean;
    error?: string;
    loading?: boolean;
  };
}

export enum SessionTabsChatStyles {
  Default = 'default',
  Conversational = 'conversational',
  /**  @deprecated */
  HighVolume = 'high_volume', // This has become Default
  /**  @deprecated */
  Standard = 'standard', // This has become Conversational
}

export interface SessionChatContentSettings {
  disable_images?: boolean;
  disable_gifs?: boolean;
  disable_emojis?: boolean; // 😢
  anonymize_usernames?: boolean;
}

export interface SessionTabsLayout {
  layout: SessionTabsLayoutTypes;
  show_icons: boolean;
  // New parameters must be optional or nullable
}

export enum SessionTabsLayoutTypes {
  NoTabs = 'No Tabs',
  Horizontal = 'Horizontal',
  Vertical = 'Vertical',
  BelowTheFold = 'Below the Fold',
  AboveTheFold = 'Above the Fold',
}

export interface CustomSocialLinks {
  [ECustomSocialLinks.X]?: string;
  [ECustomSocialLinks.LinkedIn]?: string;
  [ECustomSocialLinks.Facebook]?: string;
  [ECustomSocialLinks.Share]?: string;
}

export enum ECustomSocialLinks {
  X = 'X',
  LinkedIn = 'LinkedIn',
  Facebook = 'Facebook',
  Share = 'Share',
}

export interface SessionPreview {
  title: TranslateString;
  main_event_banner: PageModule;
  uuid: string;
  timestamp: number;
  end_timestamp: number;
  session_type: SessionTypesEnum;
  languages: string[];
  tracks: Categories;
  image?: string | null;
  description?: TranslateString;
  session: number;
  addedToMyList?: boolean;
  replay_on?: boolean;
  tags?: Session['tags'] /** uuid[] of event tag */;
  modules?: PageModule[];
  // New parameters must be optional or nullable
  custom_url_enabled?: boolean;
  custom_url?: string;
  /** @deprecated sessions now follow the event settings.design.color_theme mode */
  session_chat_dark_mode_enabled?: boolean;
  calendar_settings?: {
    content?: {
      [key: string]: {
        // Language code
        title?: string;
        description?: string;
      };
    };
  };
  requires_time_change?: TRequiresTimeChange | null;
  crew?: ISessionCrew[] | null;
}

export interface ISessionCrew {
  id: number;
  session_uuid: string;
  bl_admin: ISessionCrewAdmin;
  hidden: boolean;
}

export type ISessionCrewAdmin = Omit<BLAdmin, 'password' | 'password_reset_date'>;

export type SessionPlaybackVideo = {
  session_uuid: string;
  language: string;
  playback_url: string;
  type: 'live_stream' | 'replay' | 'placeholder_video';
};

export interface BreakoutSession {
  hosts: string[];
  create_room_when_full?: boolean; // Not used when Zoom BRs
  rooms: BreakoutRoom[];
  use_zoom: boolean;
  use_google_meet?: boolean;
  google_meet_code?: string; // passed when creating a new session to get google refresh token
  google_meet_site_url?: string; // needed for decoding the 'code'
  google_meet_use_service_account?: boolean; // use service account for creating meets rather than user account
  // New parameters must be optional or nullable
}

export type Categories = string[];

//database doesn't store everything deeply nested - will need to be assembled to match Session below
export interface DBSession {
  session: number;
  channel: number;
  title: TranslateString;
  description: TranslateString | null;
  default_language: string;
  languages: string[];
  session_type: SessionTypesEnum;
  on_demand?: boolean; // will eventually be removed in place of the session_type
  timestamp: number | null;
  tracks: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layout?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  moderation?: any;
  video: SessionVideo | null;
  /** @deprecated on_demand_video replaced by static_video  */
  on_demand_video: CreateVideo | null;
  end_timestamp: number | null;
  modules: number[];
  uuid: string;
  // ivs_channel?: IvsChannel;
  module_grouping?: PageModuleGroupModules[];
  broadcast_type: EBroadcastTypes;
  breakout_rooms?: BreakoutRoom[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  breakout_session_hosts?: any;
  breakout_sessions?: IBreakoutSession[];
  should_record: boolean | null;
  opentok_id: string | null;
  opentok_id_issued_timestamp: number | null;
  enable_feedback_survey?: boolean;
  enable_share?: boolean;
  requires_time_change?: TRequiresTimeChange | null;
  // New parameters must be optional or nullable
}

export type TRequiresTimeChange = {
  start_date: boolean;
  start_time: boolean;
  end_time: boolean;
  end_date: boolean;
};
